var ViewStatus = {
    data() {
        return {
            updating: false,
            loading: false,
            viewName: null,
            loadConfirmations: {},
            updateConfirmations: {},
            startedLoadingAt: null,
            startedUpdatingAt: null,
            minLoadingTime: 1000,
        }
    },

    mounted() {
        if (this.viewName == null) {
            throw new Error("viewName not set. ViewStatus mixin can't work without it.");
        }

        if (typeof this.loadConfirmations !== 'object') {
            throw new Error("loadConfirmations is not an object and ViewStatus mixin can't work with it. Set it to an empty if load confirmations are not required for the current view/component.");
        }

        if (typeof this.updateConfirmations !== 'object') {
            throw new Error("updateConfirmations not an object and ViewStatus mixin can't work with it. Set it to an empty object if update confirmations are not required for the current view/component.");
        }

        this.listenViewStatusEvents();
    },

    methods: {
        startLoading() {
            if (this.updating || this.loading) {
                return;
            }

            this.loading = true;
            this.resetLoadConfirmations();
            this.startedLoadingAt = (new Date).getMilliseconds();
        },

        finishLoading() {
            if (!this.loading || this.updating) {
                return;
            }

            this.loading = false;
            this.startedLoadingAt = null;
        },

        startUpdating() {
            if (this.loading || this.updating) {
                return;
            }

            this.updating = true;
            this.resetUpdateConfirmations();
            this.startedUpdatingAt = (new Date).getMilliseconds();

            let modelChangeEvent = this.viewName + ':change';
            Event.$emit(modelChangeEvent);
        },

        finishUpdating() {
            if (this.loading || !this.updating) {
                return;
            }

            this.updating = false;
            this.startedUpdatingAt = null;

            if (this.allUpdateConfirmationsAreSuccessful) {
                Event.$emit(this.everythingUpdatedSuccessfulEventName);
            }
        },

        /**
         * Increase the timesReceived property of the given source for loaded confirmations.
         */
        saveLoadConfirmation(source) {
            if (typeof this.loadConfirmations[source] == 'undefined') {
                throw new Error(source + " is not a registered load confirmation.");
            }

            this.loadConfirmations[source].timesReceived++;
        },

        /**
         * Increase the timesReceived property of the given source for updated confirmations.
         */
        saveUpdateConfirmation(source, succeeded = true) {
            if (typeof this.updateConfirmations[source] == 'undefined') {
                throw new Error(source + " is not a registered update confirmation.");
            }

            if (succeeded) {
                this.updateConfirmations[source].timesReceivedSuccessful++;
            } else {
                this.updateConfirmations[source].timesReceivedFailed++;
            }
        },

        /**
         * Reset the loading confirmations.
         */
        resetLoadConfirmations() {
            if (typeof this.loadConfirmations == 'object') {
                for (var prop in this.loadConfirmations) {
                    this.loadConfirmations[prop].timesReceived = 0;
                }
            }
        },

        /**
         * Reset the loading confirmations.
         */
        resetUpdateConfirmations() {
            if (typeof this.updateConfirmations == 'object') {
                for (var prop in this.updateConfirmations) {
                    this.updateConfirmations[prop].timesReceivedSuccessful = 0;
                    this.updateConfirmations[prop].timesReceivedFailed = 0;
                }
            }
        },

        listenViewStatusEvents() {
            // Something finished updating
            Event.$on(this.updatedEventName, (source, succeeded) => {
                this.saveUpdateConfirmation(source, succeeded);
            });

            // Something finished loading
            Event.$on(this.loadedEventName, (source) => {
                this.saveLoadConfirmation(source);
            });

            // A change was requested, start updating
            Event.$on(this.changeRequestedEventName, () => {
                this.startUpdating();
            });
        },
    },

    computed: {
        loadedEventName() {
            return this.viewName + ':loaded';
        },

        updatedEventName() {
            return this.viewName + ':updated';
        },

        changeRequestedEventName() {
            return this.viewName + ':updateRequested';
        },

        /**
         * Name of the event that is emitted when every update confirmation received is successful.
         * For example, it is useful to know when to show a success alert/message.
         */
        everythingUpdatedSuccessfulEventName() {
            return this.viewName + ':everythingUpdatedSuccessful';
        },

        allUpdateConfirmationsAreSuccessful() {
            for (var source in this.updateConfirmations) {
                if (this.updateConfirmations[source].timesReceivedFailed > 0) {
                    return false;
                }
            }

            return true;
        },
    },

    watch: {
        /**
         * Deep watch the load confirmations and stop loading ig all confirmations are received.
         */
        loadConfirmations: {
            handler: function(loadConfirmations) {
                var allConfirmationsReceived = true;

                for (var prop in loadConfirmations) {
                    if (loadConfirmations[prop].timesRequired < loadConfirmations[prop].timesReceived) {
                        allConfirmationsReceived = false;
                        return;
                    }
                }

                if (!allConfirmationsReceived) {
                    return;
                }

                // We got all the confirmations, finish loading
                let now = (new Date).getMilliseconds();
                let timeout = 0;

                let duration = now - this.startedLoadingAt;

                if (duration < this.minLoadingTime) {
                    timeout = this.minLoadingTime - duration;
                }

                setTimeout(() => {
                    this.finishLoading();
                }, timeout);
            },
            deep: true,
        },

        /**
         * Deep watch the update confirmations and stop updating if all confimrations are received.
         */
        updateConfirmations: {
            handler: function(updateConfirmations) {
                var allConfirmationsReceived = true;

                for (var prop in updateConfirmations) {
                    let timesReceived = updateConfirmations[prop].timesReceivedSuccessful + updateConfirmations[prop].timesReceivedFailed;
                    if (updateConfirmations[prop].timesRequired < timesReceived) {
                    // if (updateConfirmations[prop].timesRequired < updateConfirmations[prop].timesReceived) {
                        allConfirmationsReceived = false;
                        return;
                    }
                }

                if (!allConfirmationsReceived) {
                    return;
                }

                // We got all the confirmations, finish updating
                let now = (new Date).getMilliseconds();
                let timeout = 0;

                let duration = now - this.startedUpdatingAt;

                if (duration < this.minLoadingTime) {
                    timeout = this.minLoadingTime - duration;
                }

                setTimeout(() => {
                    this.finishUpdating();
                }, timeout);
            },
            deep: true,
        },
    },
};

export default ViewStatus;
